import { gql } from '@apollo/client';

import { fetcher } from 'hooks/useSWR';

export const PAY_ANYONE_TOKEN_RESPONSE_QUERY_POLLING = gql`
  query PayAnyoneByTokenResponsePolling($token: String!) {
    unauthenticated {
      p2p {
        pay_anyone(token: $token) {
          __typename
          ... on PayAnyoneWithExternalCard {
            claim_with_chime
            pay_friend {
              id
              ux_status
            }
          }
          ... on PayAnyoneFetchError {
            error_id
            error_header
            error_body
          }
          ... on StepUpRequiredError {
            title
            message
            challenge_context
          }
        }
      }
    }
  }
`;

export enum PollingResult {
  succeeded = 'succeeded',
  failed = 'failed',
  reversed = 'reversed',
  sent = 'sent',
  pending = 'pending',
  canceled = 'canceled',
  expired = 'expired',
  step_up_required = 'step_up_required',
}

type ErrorPageMessage = {
  error_id?: string;
  error_body?: string;
  error_header?: string;
  challenge_context?: string;
};

export const startPolling = (
  success: (result: string) => void,
  failure: (result: string | ErrorPageMessage) => void,
  url: string,
  getPollingStatus: (status: string) => PollingResult,
  graphqlData?: any
): void => {
  const timeoutMs = 20000;
  const pollingMs = 500;

  let status;
  let pollMsElapsed = 0;
  let intervalId;

  const pollTransactionStatus = async () => {
    try {
      const { apolloClient, token, useGqlGetAndClaimPayAnyone } =
        graphqlData || {};
      if (useGqlGetAndClaimPayAnyone && url.includes(`/pf/`)) {
        const { data, errors } = await apolloClient.query({
          query: PAY_ANYONE_TOKEN_RESPONSE_QUERY_POLLING,
          variables: { token },
          fetchPolicy: 'network-only',
        });
        const payAnyoneData = data?.unauthenticated?.p2p?.pay_anyone;
        status = payAnyoneData?.pay_friend?.ux_status?.toLowerCase();
        if (
          payAnyoneData?.__typename === 'PayAnyoneFetchError' ||
          payAnyoneData?.__typename === 'StepUpRequiredError'
        ) {
          throw payAnyoneData;
        }
        if (errors) throw errors;
      } else {
        const result = await fetcher(url, { method: 'GET' });
        status = getPollingStatus(result);
      }
    } catch (error) {
      clearInterval(intervalId);
      if (error.error_id === 'step_up_required_error') {
        failure({
          error_id: 'step_up_required_error',
          challenge_context: error.step_up_challenge_context,
        });
      } else {
        failure('Error fetching transaction result');
      }

      return;
    }

    switch (status as PollingResult) {
      case PollingResult.succeeded:
        clearInterval(intervalId);
        success('Transaction Claimed Successfully');
        break;
      case PollingResult.reversed:
        clearInterval(intervalId);
        failure({
          error_id: 'transaction_failed',
          error_header: 'Something went wrong',
          error_body:
            "We're sorry. Something misfired on our end. Ask your friend to send the money again.",
        });
        break;
      case PollingResult.pending:
        clearInterval(intervalId);
        failure({
          error_id: 'transaction_failed_retryable',
          error_header:
            'Something went wrong with the details you provided. Try another card',
        });
        break;
      case PollingResult.expired:
        clearInterval(intervalId);
        failure({
          error_id: 'claimed_from_expired_status',
          error_header: 'Dang! Transaction expired.',
          error_body: 'This transaction is no longer available.',
        });
        break;
      case undefined:
        clearInterval(intervalId);
        failure('Status is undefined for this transaction');
        break;
      default:
        pollMsElapsed += pollingMs;
        if (pollMsElapsed < timeoutMs) {
          intervalId = setTimeout(pollTransactionStatus, pollingMs);
        } else {
          clearInterval(intervalId);

          if (status === PollingResult.sent) {
            success(
              'Transaction claim is delayed but will most likely succeed'
            );
          } else {
            failure('Polling Timeout');
          }
        }
    }
  };

  intervalId = setTimeout(pollTransactionStatus, 0);
};

const pollTransactionStatus = (
  url: string,
  getPollingStatus: (status: string) => PollingResult,
  graphqlData?: any
): Promise<any> => {
  return new Promise((resolve, reject) => {
    startPolling(resolve, reject, url, getPollingStatus, graphqlData);
  });
};

export default pollTransactionStatus;
